/* eslint-disable no-mixed-operators */
/* eslint-disable no-bitwise */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'vanilla-lazyload';

import Head from '../Head';
import Header from '../Header';
import Footer from '../Footer';
import Scripts from '../Scripts';
// import SvgPreload from '../SvgPreload';
import Canvas from '../Canvas';

// Grab UTM codes from the url and store them in the session so they can be utilized later for tracking purposes
import '../../../assets/js/session/utm';

const getGuid = () => ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c => (
  c
      ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
).toString(16));

export const PageComponent = ({
  // CONFIG VARS
  config,
  // PAGE COMPONENT
  children,
  // HEAD PROPS
  canonical,
  description,
  headComponent,
  metaRobotsNoIndexNoFollow,
  optimizelyProjectId,
  siteTitleFirst,
  title,
  useReCaptcha,
  // HEADER PROPS
  headerIsOverlayed,
  headerLogoEyebrow,
  headerLogoEyebrowLarge,
  headerWithoutBorder,
  hideHeader,
  showInformationalBanner,
  skipNavAside,
  skipNavFooter,
  // SCRIPTS PROPS
  floodlightSendTo,
  showBingPixel,
  showChatBot,
  showFacebookPixel,
  showGooglePlaces,
  showPinterestPixel,
  // FOOTER PROPS
  appendFooter,
  customDisclaimers,
  disclaimerFootnotes,
  isAmp,
  legalCopy,
}) => {
  useEffect(() => {
    // Lazy load images
    // eslint-disable-next-line no-unused-vars
    const lazyload = new LazyLoad({ elements_selector: '.lazy' });
    // Generate a pre client id to usage in adobe and downstream pixels/lead records (tie together/back)
    const preClientId = getGuid();
    sessionStorage.setItem('pre-client-id', preClientId);
    window.adobeDataLayer = window.adobeDataLayer || [];
    window.adobeDataLayer.push({
      'user-details': {
        ids: {
          'pre-client-id': preClientId,
        },
      },
    });
    // Generate FB event ID to be sent along with FB conversion pixel events
    const fbEventId = getGuid();
    sessionStorage.setItem('fb-event-id', fbEventId);
    XMLHttpRequest.prototype.open = (function (open) {
      return function (method, url, async) {
        open.apply(this, arguments);
        if (url.indexOf('/nmx-api-proxy/nmx/') > 0) {
          this.setRequestHeader('X-Pre-Client-Id', preClientId);
        }
        if (url.indexOf('/nmx-api-proxy/nmx/ms-form-proxy/lead') > 0) {
          this.setRequestHeader('X-Fb-Event-Id', fbEventId);
        }
      };
    }(XMLHttpRequest.prototype.open));
  }, []);

  return (
    <React.Fragment>
      {/* Head properties added to the head tag with Helmet */}
      <Head
        config={config}
        Container={headComponent}
        metaRobotsNoIndexNoFollow={metaRobotsNoIndexNoFollow}
        siteTitleFirst={siteTitleFirst}
        title={title}
        description={description}
        canonical={canonical}
        optimizelyProjectId={optimizelyProjectId}
        useReCaptcha={useReCaptcha}
      />
      {/* Canvas component, which sets the max canvas size, and allows for gutters on large devices */}
      <Canvas>
        {/* Header and nav for the page */}
        <Header
          config={config}
          hideHeader={hideHeader}
          headerIsOverlayed={headerIsOverlayed}
          headerLogoEyebrow={headerLogoEyebrow}
          headerLogoEyebrowLarge={headerLogoEyebrowLarge}
          headerWithoutBorder={headerWithoutBorder}
          showInformationalBanner={showInformationalBanner}
          skipNavAside={skipNavAside}
          skipNavFooter={skipNavFooter}
        />

        {/* Main document where page components are pased down to - everything specific to the page  */}
        <main
          id="main"
          role="main"
          className="nmx-base"
          style={{
            WebkitBoxSizing: 'border-box',
            boxSizing: 'border-box',
          }}>
          {children}
        </main>

        {/* Footer and properties added for the page */}
        <Footer
          config={config}
          disclaimerFootnotes={disclaimerFootnotes}
          customDisclaimers={customDisclaimers}
          isAmp={isAmp}
          legalCopy={legalCopy}
          appendFooter={appendFooter}
        />
      </Canvas>
      {/* Page scripts */}
      <Scripts
        config={config}
        floodlightSendTo={floodlightSendTo}
        showBingPixel={showBingPixel}
        showChatBot={showChatBot}
        showFacebookPixel={showFacebookPixel}
        showGooglePlaces={showGooglePlaces}
        showPinterestPixel={showPinterestPixel}
      />

      {/* Preloaded svgs */}
      {/* TODO: look and ensure this is even helping */}
      {/* <SvgPreload /> */}
    </React.Fragment>
  );
};

PageComponent.propTypes = {
  /** Env vars used for varying components */
  config: PropTypes.shape({
    basePath: PropTypes.string.isRequired,
    nmxTemplateVersion: PropTypes.string.isRequired,
    adobeLaunchScript: PropTypes.string.isRequired,
    heapAppId: PropTypes.string.isRequired,
    newRelicApplicationId: PropTypes.string.isRequired,
    newRelicLicenseKey: PropTypes.string.isRequired,
    public: PropTypes.shape({
      googlePlacesApiKey: PropTypes.string,
      googleFloodlightsId: PropTypes.string,
      googleAdWordsId: PropTypes.string,
      googleAnalyticsTrackingId: PropTypes.string,
      recaptchaV3: PropTypes.string,
    }).isRequired,
    services: PropTypes.shape({
      loginPageUrl: PropTypes.string.isRequired,
      loginPageUrlBase: PropTypes.string.isRequired,
      userLogoutUrls: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,

  /** Component to render inside the page - everything other than header, footer, scripts, etc */
  children: PropTypes.any.isRequired,

  /** HEAD: canonical */
  canonical: PropTypes.string.isRequired,
  /** HEAD: Description to be placed in head description meta tag */
  description: PropTypes.string.isRequired,
  /** HEAD: container for head component. Replace with react-helmet or react-scripts helment if not wanting default <head /> */
  headComponent: PropTypes.any,
  /** HEAD: Do not index page with search engines */
  metaRobotsNoIndexNoFollow: PropTypes.bool,
  /** HEAD: Provide optimizely project id, if provided, will be passed to loaded optimzely script */
  optimizelyProjectId: PropTypes.string,
  /** HEAD: Should title meta tag show site title first? */
  siteTitleFirst: PropTypes.bool,
  /** HEAD: Title to be placed in head title meta tag */
  title: PropTypes.string.isRequired,
  /** HEAD: Does this page use recaptcha? If true will place script on page */
  useReCaptcha: PropTypes.bool,

  /** HEADER: Full primary nav with gradient overlay on top of body content. Coupled w/ hideHeader shows white logo overlayed on top of dark body */
  headerIsOverlayed: PropTypes.bool,
  /** HEADER: Will enable logo-only block header above body content */
  headerLogoEyebrow: PropTypes.bool,
  /** HEADER: Same as headerLogoEyebrow but larger */
  headerLogoEyebrowLarge: PropTypes.bool,
  /** HEADER: Will remove bottom header border logo-only or full nav */
  headerWithoutBorder: PropTypes.bool,
  /** HEADER: Will enable logo-only header and remove any primary navigation including login */
  hideHeader: PropTypes.bool,
  /** HEADER: showInformationalBanner */
  showInformationalBanner: PropTypes.bool,
  /** HEADER: skipNavAside */
  skipNavAside: PropTypes.bool,
  /** HEADER: skipNavFooter */
  skipNavFooter: PropTypes.bool,

  /** FOOTER: TODO - STILL BEING USED? Append additional content to footer. */
  appendFooter: PropTypes.any,
  /** FOOTER: Provide custom disclaimers to left of stock disclaimer */
  customDisclaimers: PropTypes.any,
  /** FOOTER: Provide custom disclaimer footnotes to left of stock disclaimer */
  disclaimerFootnotes: PropTypes.any,
  /** FOOTER: Is this an amp page? */
  isAmp: PropTypes.bool,
  /** FOOTER: TODO - STILL BEING USED? Use legal copy. */
  legalCopy: PropTypes.any,

  /** SCRIPTS: Location to send to floodlight */
  floodlightSendTo: PropTypes.string,
  /** SCRIPTS: Show and add bing pixel script */
  showBingPixel: PropTypes.bool,
  /** SCRIPTS: Show chatbot */
  showChatBot: PropTypes.bool,
  /** SCRIPTS: Show and add facebook pixel script */
  showFacebookPixel: PropTypes.bool,
  /** SCRIPTS: Show and add google places script */
  showGooglePlaces: PropTypes.bool,
  /** SCRIPTS: Show and add pinterest pixel script */
  showPinterestPixel: PropTypes.bool,
  /** SCRIPTS: Show and add recaptcha script */
};

PageComponent.defaultProps = {
  metaRobotsNoIndexNoFollow: false,
  siteTitleFirst: false,

  headerIsOverlayed: false,
  headerWithoutBorder: false,
  hideHeader: false,
  showInformationalBanner: true,
  skipNavAside: false,
  skipNavFooter: false,

  isAmp: false,

  showBingPixel: true,
  showChatBot: false,
  showFacebookPixel: true,
  showGooglePlaces: false,
  showPinterestPixel: true,
  useReCaptcha: false,
};

export default PageComponent;
